import { useParams } from 'react-router-dom';
import { useMe } from '@/lib/hooks';
import { renderDiscordText } from '@/lib/discordText';
import { useMentionNames, type MentionNames } from '@/lib/mentions';

export interface PreviewEmbed {
  title?: string;
  description?: string;
  color?: number;
  imageUrl?: string;
  thumbnailUrl?: string;
  author?: { name: string; iconUrl?: string; url?: string };
  footer?: { text: string; iconUrl?: string };
  timestamp?: boolean | string;
}

interface Props {
  /** Values injected into the {PLACEHOLDER} substitutions across content + embed */
  placeholders?: Record<string, string>;
  botName?: string;
  botAvatarUrl?: string | null;
  content?: string;
  embed?: PreviewEmbed;
  /** Extra hint text under the header (e.g. "Vorschau — {USER} = du selbst"). */
  hint?: string;
}

/**
 * Discord-style message preview used by all message-producing config pages
 * (Welcome, Leave, Leveling, Tickets, Notifications, ModMail). Renders
 * *approximately* what the Discord client draws when the bot posts.
 *
 * Placeholders like {USER}, {SERVER}, {MEMBERCOUNT}, {LEVEL} are substituted
 * live from the `placeholders` prop so the preview stays honest as the user
 * edits the template.
 */
export function DiscordMessagePreview({
  placeholders = {},
  botName = 'Bot',
  botAvatarUrl,
  content,
  embed,
  hint,
}: Props) {
  const me = useMe();
  const now = new Date();
  const { guildId } = useParams<{ guildId: string }>();
  const names = useMentionNames(guildId, [content, embed?.title, embed?.description]);

  const defaultPlaceholders = {
    USER: `@${me.data?.user?.username ?? 'user'}`,
    USERNAME: me.data?.user?.username ?? 'user',
    SERVER: 'Mein Server',
    MEMBERCOUNT: '42',
    LEVEL: '5',
    ...placeholders,
  };

  const substitute = (s?: string) => {
    if (!s) return s;
    return s.replace(/\{(\w+)\}/g, (_m, key) => {
      const v = defaultPlaceholders[key as keyof typeof defaultPlaceholders];
      return v ?? `{${key}}`;
    });
  };

  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Vorschau
      </h2>
      {hint && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
      <div className="rounded-lg bg-[#313338] p-4 text-white">
        <div className="flex items-start gap-3">
          {botAvatarUrl ? (
            <img
              src={botAvatarUrl}
              alt=""
              className="h-10 w-10 flex-shrink-0 rounded-full"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#5865f2] text-sm font-semibold">
              {botName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-white">{botName}</span>
              <span className="rounded bg-[#5865f2] px-1 text-[10px] font-semibold uppercase text-white">
                Bot
              </span>
              <span className="text-xs text-white/50">
                heute um {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>

            {content && substitute(content) && (
              <div className="mt-1 whitespace-pre-wrap break-words text-sm text-white/95">
                {renderDiscordText(substitute(content), names)}
              </div>
            )}

            {embed && <EmbedBlock embed={embed} substitute={substitute} names={names} />}

            {!content && !embed && (
              <div className="mt-1 text-sm italic text-white/40">Nichts zu zeigen.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmbedBlock({
  embed,
  substitute,
  names,
}: {
  embed: PreviewEmbed;
  substitute: (s?: string) => string | undefined;
  names: MentionNames;
}) {
  const bar = '#' + ((embed.color ?? 0x5865f2) & 0xffffff).toString(16).padStart(6, '0');
  const timestampStr =
    embed.timestamp === true
      ? new Date().toLocaleString('de-DE')
      : typeof embed.timestamp === 'string'
        ? isNaN(new Date(embed.timestamp).getTime())
          ? ''
          : new Date(embed.timestamp).toLocaleString('de-DE')
        : '';

  return (
    <div
      className="mt-2 flex overflow-hidden rounded border-l-4 bg-[#2b2d31] text-sm"
      style={{ borderLeftColor: bar }}
    >
      <div className="min-w-0 flex-1 p-3">
        {embed.author?.name && (
          <div className="mb-1 flex items-center gap-1.5 text-xs">
            {embed.author.iconUrl && (
              <img src={embed.author.iconUrl} alt="" className="h-4 w-4 rounded-full" />
            )}
            <span className="font-medium text-white">{substitute(embed.author.name)}</span>
          </div>
        )}
        {embed.title && (
          <div className="mb-1 font-semibold text-white">{renderDiscordText(substitute(embed.title), names)}</div>
        )}
        {embed.description && (
          <div className="mb-2 whitespace-pre-wrap break-words text-white/90">
            {renderDiscordText(substitute(embed.description), names)}
          </div>
        )}
        {embed.imageUrl && (
          <img
            src={embed.imageUrl}
            alt=""
            className="mt-2 max-h-72 rounded-md"
          />
        )}
        {(embed.footer?.text || timestampStr) && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-white/60">
            {embed.footer?.iconUrl && (
              <img src={embed.footer.iconUrl} alt="" className="h-4 w-4 rounded-full" />
            )}
            {embed.footer?.text && <span>{substitute(embed.footer.text)}</span>}
            {embed.footer?.text && timestampStr && <span>•</span>}
            {timestampStr && <span>{timestampStr}</span>}
          </div>
        )}
      </div>
      {embed.thumbnailUrl && (
        <img
          src={embed.thumbnailUrl}
          alt=""
          className="m-3 h-20 w-20 flex-shrink-0 rounded-md object-cover"
        />
      )}
    </div>
  );
}
